import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EnergyApiService from '../../api/EnergyApiService';
import LocationApiService from '../../api/LocationApiService';
import OrgApiService from '../../api/OrgApiService';
import AuthUtil from '../Utils/AuthUtil';
import { useSnackbar } from '../Common/SnackbarProvider';
import ProductionEntryGrid from './ProductionEntryGrid';
import UtilityBillEntryGrid from './UtilityBillEntryGrid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';

const energyApiService = new EnergyApiService();
const locationApiService = new LocationApiService();
const orgApiService = new OrgApiService();

const PRODUCTION_TAB = 0;
const UTILITIES_TAB = 1;

/**
 * Earliest year the picker allows on a site without older data, so a typo like "202" cannot open
 * an empty year in the distant past. A site whose data predates it reaches back to that data.
 */
const EARLIEST_ENTRY_YEAR = 2000;

/**
 * Manual entry of the figures the energy module runs on: what a location produced and sold, and
 * what its meters used. One location and year at a time, because that is how customers report.
 * Admin only (see the route), so whoever reaches the page can edit what it shows.
 */
export default function SiteData () {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);

    // Super admins enter data on behalf of any customer, so they pick the organization first.
    // Organization admins are fixed to their own organization and never see the picker.
    const [organizations, setOrganizations] = useState([]);
    const [orgId, setOrgId] = useState(() => AuthUtil.getUsersOrgId(userToken) ?? '');
    const [locations, setLocations] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState('');
    const [meters, setMeters] = useState([]);
    const [units, setUnits] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [firstDataYear, setFirstDataYear] = useState(null);
    const [tab, setTab] = useState(PRODUCTION_TAB);

    useEffect(() => {
        if (!isSuperAdmin) return;

        async function loadOrganizations () {
            const response = await orgApiService.getOrgs();
            const orgs = [...(response?.data ?? [])].sort((a, b) => a.ogName.localeCompare(b.ogName));
            setOrganizations(orgs);

            // Start on the super admin's own organization when it is in the list, else the first.
            setOrgId(current => (orgs.some(org => org.ogId === current) ? current : (orgs[0]?.ogId ?? '')));
        }

        loadOrganizations();
    }, [isSuperAdmin]);

    useEffect(() => {
        async function loadLocations () {
            if (isSuperAdmin && orgId === '') return;

            const response = isSuperAdmin
                ? await locationApiService.getFullLocationsForOrg(orgId)
                : await locationApiService.getFullLocationsForCurrentOrg();
            const projects = (response?.data ?? []).filter(project => project.pjActive > 0);
            setLocations(projects);

            // Keep the chosen location if it belongs to this organization; otherwise move to the
            // first one, so switching organization never leaves another customer's location selected.
            setProjectId(current => (projects.some(p => p.pjId === current) ? current : (projects[0]?.pjId ?? '')));
        }

        loadLocations();
    }, [isSuperAdmin, orgId]);

    useEffect(() => {
        async function loadUnits () {
            const response = await energyApiService.getUnits();
            if (response?.success) {
                setUnits(response.data);
            }
        }

        loadUnits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSites = useCallback(async () => {
        if (!projectId) {
            // An organization with no locations must not keep showing the previous one's sites.
            setSites([]);
            setSiteId('');
            return;
        }

        const response = await energyApiService.getSites(projectId);
        if (!response?.success) {
            snackbar.error(response?.message ?? t('siteData.toast.loadFailed'));
            setSites([]);
            return;
        }

        setSites(response.data);
        setSiteId(response.data.length > 0 ? response.data[0].id : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => { loadSites(); }, [loadSites]);

    useEffect(() => {
        async function loadMeters () {
            if (!siteId) {
                setMeters([]);
                return;
            }

            const response = await energyApiService.getMeters(siteId);
            setMeters(response?.success ? response.data.filter(meter => meter.isActive) : []);
        }

        loadMeters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteId]);

    useEffect(() => {
        async function loadFirstDataYear () {
            if (!siteId) {
                setFirstDataYear(null);
                return;
            }

            // On failure the picker still offers the standard window, so there is nothing to report.
            const response = await energyApiService.getFirstDataYear(siteId);
            setFirstDataYear(response?.success ? response.data : null);
        }

        loadFirstDataYear();
    }, [siteId]);

    const minYear = Math.min(EARLIEST_ENTRY_YEAR, firstDataYear ?? EARLIEST_ENTRY_YEAR);
    const maxYear = new Date().getFullYear();

    // Typing passes through partial years ("2", "20", ...); only a whole year in range is taken.
    const changeYear = value => {
        const picked = value?.isValid() ? value.year() : null;
        if (picked !== null && picked >= minYear && picked <= maxYear) {
            setYear(picked);
        }
    };

    const site = useMemo(() => sites.find(s => s.id === siteId) ?? null, [sites, siteId]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>
            <Box>
                <Typography variant="h4">{t('siteData.title')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('siteData.subtitle')}</Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {isSuperAdmin && (
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                            <InputLabel id="organization-label">{t('fieldName.organization')}</InputLabel>
                            <Select
                                labelId="organization-label"
                                label={t('fieldName.organization')}
                                value={organizations.length > 0 ? orgId : ''}
                                onChange={event => setOrgId(event.target.value)}
                            >
                                {organizations.map(org => (
                                    <MenuItem key={org.ogId} value={org.ogId}>{org.ogName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl size="small" sx={{ minWidth: 260 }} disabled={locations.length === 0}>
                        <InputLabel id="location-label">{t('fieldName.location')}</InputLabel>
                        <Select
                            labelId="location-label"
                            label={t('fieldName.location')}
                            value={projectId}
                            onChange={event => setProjectId(event.target.value)}
                        >
                            {locations.map(location => (
                                <MenuItem key={location.pjId} value={location.pjId}>{location.pjName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 240 }} disabled={sites.length === 0}>
                        <InputLabel id="site-label">{t('siteData.site')}</InputLabel>
                        <Select
                            labelId="site-label"
                            label={t('siteData.site')}
                            value={siteId}
                            onChange={event => setSiteId(event.target.value)}
                        >
                            {sites.map(option => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name ? `${option.code} · ${option.name}` : option.code}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            views={['year']}
                            openTo="year"
                            inputFormat="YYYY"
                            label={t('siteData.year')}
                            minDate={dayjs(new Date(minYear, 0, 1))}
                            maxDate={dayjs(new Date(maxYear, 11, 31))}
                            value={dayjs(new Date(year, 0, 1))}
                            onChange={changeYear}
                            renderInput={params => <TextField {...params} size="small" sx={{ width: 140 }} />}
                        />
                    </LocalizationProvider>
                </Stack>
            </Paper>

            {orgId !== '' && locations.length === 0 && <Alert severity="info">{t('siteData.noLocations')}</Alert>}
            {projectId !== '' && sites.length === 0 && <Alert severity="info">{t('siteData.noSites')}</Alert>}

            {/* Every figure is measured against a site's meters, so with no sites there is nothing to
                enter and the notice above says why. */}
            {sites.length > 0 && (
                <Paper variant="outlined">
                    <Tabs value={tab} onChange={(event, value) => setTab(value)}>
                        <Tab label={t('siteData.tab.production')} id="site-data-tab-0" />
                        <Tab label={t('siteData.tab.utilities')} id="site-data-tab-1" />
                    </Tabs>

                    <Box sx={{ p: 2 }} role="tabpanel" hidden={tab !== PRODUCTION_TAB}>
                        {tab === PRODUCTION_TAB && (
                            <ProductionEntryGrid
                                projectId={projectId}
                                siteId={siteId === '' ? undefined : siteId}
                                year={year}
                                canEdit
                            />
                        )}
                    </Box>

                    <Box sx={{ p: 2 }} role="tabpanel" hidden={tab !== UTILITIES_TAB}>
                        {/* Readings hang off a meter, which hangs off a site, so say which piece is
                            missing rather than showing an empty tab. */}
                        {tab === UTILITIES_TAB && (site
                            ? (
                                <UtilityBillEntryGrid
                                    siteId={siteId}
                                    year={year}
                                    canEdit
                                    meters={meters}
                                    units={units}
                                />
                            )
                            : <Alert severity="info">{t('siteData.selectSite')}</Alert>)}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
